<template>
  <InputComponent :label="$attrs.label" :sublabel="$attrs.sublabel" v-bind="$attrs">
    <SelectComponent
      :value="value"
      class="field-select-modal elder-input__element"
      :class="`field-select-modal--variant-${variant}`"
      @input="onInput"
      v-bind="$attrs"
      :title="$attrs.label"
      :readonly="$attrs.disabled || $attrs.readonly"
      :compare="compare"
      :items="getItems"
    >
      <template #item="{ item }">
        <Loader :value="promise" :icon="['fal', 'spinner-third']">
          <slot name="item" :item="item">
            <div class="field-select-modal__item" :class="`field-select-modal__item--${itemStyle}`">
              <img
                v-if="getProperty(item, 'thumbnail')"
                :src="getProperty(item, 'thumbnail')"
                :alt="getProperty(item, 'label', { fallbackKey: 'title' })"
                class="field-select-modal__item-thumbnail"
              />

              <div class="field-select-modal__item-icon" v-else-if="getProperty(item, 'id')">
                <Icon :icon="getProperty(item, 'icon') || ['fal', 'image']" size="lg" />
              </div>

              <div class="field-select-modal__item-details">
                <strong
                  class="field-select-modal__item-title"
                  v-html="$t(getProperty(item, 'label', { fallbackKey: 'title' }))"
                ></strong>
                <span
                  v-if="getProperty(item, 'subtitle')"
                  class="field-select-modal__item-subtitle"
                  v-html="getProperty(item, 'subtitle')"
                ></span>
              </div>
              <Icon
                v-if="value === item"
                class="field-select-modal__item-select-icon"
                :icon="['fal', 'angle-down']"
                size="lg"
              />
            </div>
          </slot>
        </Loader>
      </template>

      <template #empty>
        <span class="fade"
          >{{ $attrs.emptyLabel || $attrs['empty-label'] || '...' }}
          <Icon class="field-select-modal__empty-icon" :icon="['fal', 'angle-down']" size="lg" />
        </span>
      </template>

      <template #noresults="{ filter }">
        <slot name="noresults" :filter="filter">
          <span>-</span>
        </slot>
      </template>
      <template #actions>
        <slot name="actions"></slot>
      </template>
    </SelectComponent>
  </InputComponent>
</template>

<script>
import SelectComponent from '@kvass/selector'
import { LoaderComponent as Loader } from 'vue-elder-loader'
import { InputComponent } from 'vue-elder-input'
import { CurlyTemplateReplacer } from '../utils.js'
import Icon from '@kvass/vue2-icon'

export default {
  name: 'Select',
  props: {
    value: [String, Array],
    items: [String, Array, Function],
    default: [String],
    variant: {
      type: String,
      enums: ['list', 'grid'],
      default: 'list',
    },
    itemStyle: {
      type: String,
      enums: ['circle', 'square'],
      default: 'circle',
    },
  },
  watch: {
    value: {
      handler: 'setMap',
      immediate: true,
    },
  },
  data() {
    return {
      promise: null,
      map: {},
    }
  },
  methods: {
    getProperty(val, key, options = {}) {
      const { fallbackKey } = options
      if (!val) return
      if (typeof val !== 'string') return val[key] || val[fallbackKey]
      return (this.map[val] && this.map[val][key]) || (this.map[val] && this.map[val][fallbackKey])
    },
    onInput(val) {
      if (!val) return this.$emit('input', null)
      if (val instanceof Array)
        return this.$emit(
          'input',
          val.map(v => v.id || v),
        )
      return this.$emit('input', val.id)
    },
    async getItems(filter = {}) {
      if (this.items instanceof Array)
        return await Promise.all(
          this.items.map(i => {
            if (i.url) {
              return fetch(this.getUrl(i.url, filter)).then(res => res.json())
            }
            return i
          }),
        ).then(res => res.flat())
      if (typeof this.items === 'function') return this.items()

      return fetch(this.getUrl(this.items, filter)).then(res => res.json())
    },
    setMap(id) {
      if (!id || !id.length) return (this.map = {})
      this.promise = this.getItems({ ids: id instanceof Array ? id : [id] }).then(
        items => (this.map = Object.fromEntries(items.map(item => [item.id, item]))),
      )
    },
    compare(a, b) {
      let getId = x => (typeof x === 'string' ? x : x.id)
      return getId(a) === getId(b)
    },
    getUrl(item, filter) {
      let rawUrl = item.startsWith('http') ? item : `${location.origin}${item}`
      let url = new URL(CurlyTemplateReplacer(rawUrl, this.$attrs['schema-data'] || {}))
      Object.entries(filter)
        .filter(([k, v]) => v !== null)
        .forEach(([k, v]) => {
          switch (v.constructor) {
            case Array:
              return v.forEach(x => url.searchParams.append(k, x))
            default:
              return url.searchParams.append(k, v)
          }
        })

      return url.toString()
    },
  },
  created() {
    let hasDefaultProp = 'default' in this.$options.propsData
    if (hasDefaultProp && this.value === undefined) this.$emit('input', this.default)
  },
  components: {
    SelectComponent,
    InputComponent,
    Loader,
    Icon,
  },
}
</script>

<style lang="scss">
.field-select-modal {
  &[disabled='disabled'] {
    cursor: not-allowed;
  }

  &__empty-icon {
    position: absolute;
    right: 1.1rem;
    top: 50%;
    transform: translateY(-45%);
    color: inherit;
    opacity: 0.5;
    pointer-events: none;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 1rem;

    &-thumbnail {
      aspect-ratio: 1 / 1;
      width: 40px;

      object-fit: cover;
    }
    &-icon {
      width: 40px;
      height: 40px;

      display: flex;
      justify-content: center;
      align-items: center;

      background: var(--light-grey);
      border-radius: 50%;
      svg {
      }
    }
    &-details {
      display: flex;
      flex-direction: column;
    }

    &-subtitle {
      opacity: 0.7;
    }

    &--circle {
      .field-select-modal__item-thumbnail {
        border-radius: 30rem;
      }
    }

    &--square {
      .field-select-modal__item-thumbnail {
        object-fit: contain;
      }
    }
  }

  .kvass-selector__modal-card {
    width: 100%;
    max-width: 500px;
  }

  .kvass-card__footer-content {
    justify-content: center;
  }

  .kvass-selector__item + .kvass-selector__item {
    margin: 0;
  }

  .kvass-selector__input .elder-loader.elder-loader--active:not(:first-child) {
    display: none;
  }

  .elder-loader__loading-message {
    display: none;
  }
  .elder-loader__element-content-inner {
    min-width: initial;
    padding: 0;
    margin: 0;
  }

  &--variant {
    &-list {
      .kvass-selector {
        &__item {
          border-top: 1px solid var(--light-grey);
        }
      }
    }

    &-grid {
      .kvass-selector {
        &__modal-card {
          max-width: 800px;
        }
        &__items {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        &__item {
          border: 1px solid #aeb2b6;

          padding: 1rem;
          &--selected {
            border: 1px solid var(--primary) !important;
          }
          .field-select-modal__item {
            gap: 2rem;

            img {
              width: 65px;
              border-radius: 0;
              object-fit: contain;
            }
          }
        }
      }
    }
  }
}
</style>
